// @ts-nocheck
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { GetAccountApiParameter } from "@insite/client-framework/Services/AccountService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccount";
import setUserFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetUserFields";
import { getCurrentEditingUser } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
/* Store Custom Code - Start */
import siteMessage from "@insite/client-framework/SiteMessage";
import useIsApprovalRequired from "@store/Framework/Store/Data/Accounts/Hooks/useIsApprovalRequired";
/* Store Custom Code - End */

/* Starter Custom Code - Start */
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { formGridProps } from "@store/Themes/common/props/form";
/* Starter Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    editingUser: getCurrentEditingUser(state),
});

const mapDispatchToProps = {
    setUserFields,
    loadAccount,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UserSetupSettingsStyles {
    title?: TypographyPresentationProps;
    fieldset?: InjectableCss;
    wrapper?: InjectableCss;
    userRoleSelect?: SelectPresentationProps;
    approverSelect?: SelectPresentationProps;
    approverDynamicDropdown?: DynamicDropdownPresentationProps;
    // Starter Custom Code
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    // Store Custom Code
    userRoleDynamicDropdown?: DynamicDropdownPresentationProps;
}

export const userSetupSettingsStyles: UserSetupSettingsStyles = {
    title: formGridProps.title,
    container: formGridProps.gridContainer,
    gridItem: { width: [12, 6, 6, 6, 6] },
};

const styles = userSetupSettingsStyles;
const StyledFieldSet = getStyledWrapper("div"); // Starter Custom Code

const UserSetupSettings = ({ editingUser, setUserFields, loadAccount }: Props) => {
    // Starter Moved - if (!editingUser) { - down

    const [options, setOptions] = useState<OptionObject[]>([]);

    useEffect(() => {
        /* Starter Custom Code - Start */
        if (!editingUser) {
            return;
        }
        /* Starter Custom Code - End */

        if (options.length < 1) {
            setApproverOptions("");
        }

        loadAccount({ accountId: editingUser.id, numberOfApprovers: 20 });
    }, [editingUser]);
    /* Store Custom Code */
    const getIsApprovalRequired = useIsApprovalRequired();

    /* Starter Custom Code - Start */
    if (!editingUser) {
        return null;
    }
    /* Starter Custom Code - End */
    /* Store Custom Code - Start */
    const userRoleChangeHandler = (value?: string) => {
        setUserFields({ role: value });
        setUserFields({ approver: "" });
    };
    /* Store Custom Code - End */

    const approverChangeHandler = (value?: string) => {
        const approverValue = value || "";
        setUserFields({ approver: approverValue });
    };

    const onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const filterValue = event.currentTarget.value;
        debouncedSearchApprovers(filterValue);
    };

    const debouncedSearchApprovers = debounce((filterValue: string) => {
        setApproverOptions(filterValue);
    }, 300);

    const setApproverOptions = (filterValue: string) => {
        const currentAccountId = editingUser.id;
        if (!currentAccountId) {
            return;
        }

        let approverOptions: OptionObject[] = [{ optionText: translate("Select an Approver"), optionValue: "" }];
        const parameter = {
            accountId: currentAccountId,
            numberOfApprovers: 20,
        } as GetAccountApiParameter;

        if (filterValue !== "") {
            parameter.approversUsernameFilter = filterValue;
        }

        loadAccount({
            ...parameter,
            onComplete: result => {
                if (!result?.apiResult?.availableApprovers) {
                    return;
                }

                approverOptions = approverOptions.concat(
                    result.apiResult.availableApprovers.map(o => ({ optionText: o, optionValue: o })),
                );

                setOptions(approverOptions);
            },
        });
    };
    /* Store Custom Code - Start */
    const isApprovalRequired = getIsApprovalRequired(editingUser.role);
    const approverErrorMessage =
        isApprovalRequired && !editingUser.approver ? siteMessage("User_Admin_Info_approver_Required") : undefined;
    const roleErrorMessage = editingUser.role ? undefined : siteMessage("User_Admin_Info_role_Required");
    const roleOptions: OptionObject[] = [
        { optionText: translate("Select User Role"), optionValue: "" },
        ...(editingUser.availableRoles || []).map(role => ({
            optionText:
                (editingUser.availableRolesTranslated && editingUser.availableRolesTranslated[role.toLowerCase()]) ||
                role,
            optionValue: role,
        })),
    ];

    /* Store Custom Code - End */
    return (
        <>
            <Typography {...styles.title} data-test-selector="userSetupSetting_title" /* Starter Custom Code */>
                {translate("Settings")}
            </Typography>
            <StyledFieldSet {...styles.fieldset}>
                <GridContainer {...styles.container} /* Starter Custom Code */>
                    <GridItem {...styles.gridItem} /* Starter Custom Code */>
                        <DynamicDropdown
                            {...styles.userRoleDynamicDropdown}
                            label={translate("User Role")}
                            selected={editingUser.role}
                            onSelectionChange={userRoleChangeHandler}
                            data-test-selector="userSetupSetting_userRole"
                            options={roleOptions} /* Store Custom Code */
                            required /* Store Custom Code */
                            error={roleErrorMessage} /* Store Custom Code */
                        />
                    </GridItem>
                    <GridItem {...styles.gridItem} /* Starter Custom Code */>
                        <DynamicDropdown
                            {...styles.approverDynamicDropdown}
                            label={translate("Assign Approver")}
                            onInputChange={onInputChanged}
                            onSelectionChange={approverChangeHandler}
                            selected={editingUser.approver}
                            options={options}
                            required={isApprovalRequired} /* Store Custom Code */
                            data-test-selector="userSetupSetting_assignApprover"
                            placeholder={translate("Select an Approver")}
                            error={approverErrorMessage} /* Store Custom Code */
                        />
                    </GridItem>
                </GridContainer>
            </StyledFieldSet>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserSetupSettings),
    definition: {
        group: "User Setup",
        displayName: "Settings",
        allowedContexts: ["UserSetupPage"],
    },
};

export default widgetModule;
